/**
 * `heysegment images` — upload and list workspace images.
 *
 *   heysegment images upload <file...>   → optimize + upload; prints the public URL
 *   heysegment images list               → the workspace's images as markdown
 *
 * The workspace is never named on the wire — the server derives it from the
 * authenticated session. Upload runs the local optimizer (resize + re-encode)
 * by default, then presigns → PUTs to R2 → registers the images row. When a
 * `--title` is given the CLI's metadata is authoritative and the server's AI
 * describer is skipped; otherwise the server auto-describes the image.
 *
 * @module
 */
import { Console, Effect, FileSystem, Option } from "effect"
import { Argument, Command, Flag } from "effect/unstable/cli"
import { FetchHttpClient } from "effect/unstable/http"
import type { CliImage } from "@repo/contracts/rpc/procedures/session"
import { withSession, step } from "../session/with-session"
import { OperationFailed, UsageError } from "../errors"
import { SessionClient } from "../rpc"
import * as transfer from "../session/transfer"
import { mimeFromPath, optimizeImage } from "../images/optimize"
import { baseName, formatBytes } from "../format"
import { apiUrlFlag, dirFlag, jsonFlag } from "./flags"

// ── Result shape ────────────────────────────────────────────────────────

export interface UploadedImage {
  readonly file: string
  readonly id: string
  readonly type: string
  readonly url: string
  readonly title: string | null
  readonly description: string | null
  readonly width: number | null
  readonly height: number | null
  readonly fileSize: number | null
  /** True when the server dispatched AI title/description generation. */
  readonly analyzing: boolean
  /** True when the CLI resized / re-encoded before upload (vs. shipped source). */
  readonly optimized: boolean
}

// ── Validation ──────────────────────────────────────────────────────────

/**
 * Validate the metadata flags. Returns a human error reason, or null when the
 * combination is valid. Extracted so the rules are unit-testable without a
 * session. Metadata is per-image (single file only), and a description alone
 * would be clobbered by the auto-describer, so it requires a title.
 */
export const validateUploadArgs = (input: {
  readonly fileCount: number
  readonly title: string | undefined
  readonly description: string | undefined
}): string | null => {
  if (
    (input.title !== undefined || input.description !== undefined) &&
    input.fileCount > 1
  ) {
    return "--title / --description apply to a single image. Upload one file at a time to set them."
  }
  if (input.description !== undefined && input.title === undefined) {
    return "--description requires --title (a description alone is overwritten by the auto-generated title)."
  }
  return null
}

// ── Formatting ──────────────────────────────────────────────────────────

export const formatUploadedLine = (r: UploadedImage): string => {
  const dims = r.width !== null && r.height !== null ? `${r.width}×${r.height}` : "dimensions unknown"
  const shape = r.optimized ? "optimized" : "original"
  const describing = r.analyzing ? " · describing…" : ""
  return `✓ ${r.file} → ${r.url}  (${dims}, ${formatBytes(r.fileSize)}, ${shape})${describing}`
}

const escapeAttribute = (value: string): string =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")

export const imageEmbedSnippet = (input: {
  readonly url: string
  readonly title: string | null
  readonly description: string | null
}): string => {
  const alt = input.description ?? input.title ?? ""
  return `<img src="${escapeAttribute(input.url)}" alt="${escapeAttribute(alt)}" />`
}

export const renderImageList = (images: ReadonlyArray<CliImage>): string => {
  const lines: Array<string> = [
    `${images.length} image${images.length === 1 ? "" : "s"} in this workspace. Reference any by its URL in an \`<img src>\`.`,
    "",
  ]
  for (const img of images) {
    const title = img.title ?? "(describing…)"
    const desc = img.description !== null && img.description.length > 0 ? ` — ${img.description}` : ""
    lines.push(`- **${title}**${desc}`)
    lines.push(`  ${img.url}`)
    lines.push(`  ${imageEmbedSnippet(img)}`)
  }
  return lines.join("\n")
}

// ── Core: upload one file ───────────────────────────────────────────────

const uploadOne = (
  file: string,
  meta: { readonly title: string | undefined; readonly description: string | undefined },
  opts: { readonly disabled: boolean; readonly maxEdge: number | undefined },
): Effect.Effect<
  UploadedImage,
  OperationFailed | UsageError,
  SessionClient | FileSystem.FileSystem
> =>
  Effect.gen(function* () {
    const fs = yield* FileSystem.FileSystem
    const client = yield* SessionClient

    const sourceMime = mimeFromPath(file)
    if (sourceMime === null) {
      return yield* Effect.fail(
        new UsageError({
          reason: `Not a recognized image file: ${file} (expected png, jpg, webp, gif, svg, avif, heic, bmp, tiff).`,
        }),
      )
    }

    const bytes = yield* step(`images:read ${baseName(file)}`, fs.readFile(file))

    const optimized = yield* optimizeImage(
      { bytes, sourceMime },
      { disabled: opts.disabled, maxLongestEdge: opts.maxEdge },
    )

    const presigned = yield* step(
      `images:presign ${baseName(file)}`,
      client.presignImageUpload({ mimeType: optimized.mime, fileName: baseName(file) }),
    )

    yield* step(
      `images:upload ${baseName(file)}`,
      transfer
        .uploadBytes({ url: presigned.url, bytes: optimized.bytes, contentType: optimized.mime })
        .pipe(Effect.provide(FetchHttpClient.layer)),
    )

    const confirmed = yield* step(
      `images:register ${baseName(file)}`,
      client.registerImageUpload({
        imageId: presigned.imageId,
        width: optimized.width ?? undefined,
        height: optimized.height ?? undefined,
        title: meta.title,
        description: meta.description,
      }),
    )

    // Best-effort telemetry — never fail the upload on a reporting hiccup.
    yield* client
      .report({ type: "image.uploaded", data: { id: confirmed.id } })
      .pipe(Effect.ignore)

    const uploaded: UploadedImage = {
      file,
      id: confirmed.id,
      type: confirmed.type,
      url: confirmed.url,
      title: confirmed.title,
      description: confirmed.description,
      width: confirmed.width,
      height: confirmed.height,
      fileSize: confirmed.fileSize,
      analyzing: confirmed.analyzing,
      optimized: optimized.optimized,
    }
    return uploaded
  })

// ── Core: the `upload` program (testable without withSession) ───────────

export const runUpload = (input: {
  readonly files: ReadonlyArray<string>
  readonly title: Option.Option<string>
  readonly description: Option.Option<string>
  readonly optimize: boolean
  readonly maxEdge: Option.Option<number>
  readonly json: boolean
}): Effect.Effect<
  void,
  OperationFailed | UsageError,
  SessionClient | FileSystem.FileSystem
> =>
  Effect.gen(function* () {
    const title = Option.getOrUndefined(input.title)
    const description = Option.getOrUndefined(input.description)

    const invalid = validateUploadArgs({
      fileCount: input.files.length,
      title,
      description,
    })
    if (invalid !== null) {
      return yield* Effect.fail(new UsageError({ reason: invalid }))
    }

    const maxEdge = Option.getOrUndefined(input.maxEdge)
    const results: Array<UploadedImage> = []
    for (const file of input.files) {
      const uploaded = yield* uploadOne(
        file,
        { title, description },
        { disabled: !input.optimize, maxEdge },
      )
      results.push(uploaded)
    }

    // Make the (deterministic) metadata behaviour explicit — the same notes go
    // to stdout for humans and into the JSON payload for agents.
    const analyzing = results.filter((r) => r.analyzing).length
    const noAlt = results.filter(
      (r) =>
        !r.analyzing &&
        r.title !== null &&
        (r.description === null || r.description.length === 0),
    ).length
    const notes: Array<string> = []
    if (analyzing > 0) {
      notes.push(
        `${analyzing} image${analyzing === 1 ? " is" : "s are"} being auto-described (title + alt text generated from the image); title/description are null now and filled in later — re-run \`heysegment images list\` to read them.`,
      )
    }
    if (noAlt > 0) {
      notes.push(
        `${noAlt} image${noAlt === 1 ? " has" : "s have"} no alt text — a --title was given, so auto-describe is off. Add alt text with --description.`,
      )
    }

    if (input.json) {
      yield* Console.log(JSON.stringify({ ok: true, images: results, notes }, null, 2))
      return
    }
    for (const r of results) {
      yield* Console.log(formatUploadedLine(r))
      yield* Console.log(`  ${imageEmbedSnippet(r)}`)
    }
    for (const note of notes) {
      yield* Console.log(`\nℹ ${note}`)
    }
  })

// ── Core: the `list` program ────────────────────────────────────────────

export const runList = (input: {
  readonly json: boolean
}): Effect.Effect<void, OperationFailed, SessionClient> =>
  Effect.gen(function* () {
    const client = yield* SessionClient
    const { images } = yield* step("images:list", client.listImages(void 0))

    if (input.json) {
      yield* Console.log(JSON.stringify({ ok: true, images }, null, 2))
      return
    }
    if (images.length === 0) {
      yield* Console.log(
        "No images in this workspace yet. Add one with:\n" +
          "  heysegment images upload <file>                              # server auto-generates title + alt text\n" +
          '  heysegment images upload <file> --title "…" --description "…"  # your metadata, stored verbatim',
      )
      return
    }
    yield* Console.log(renderImageList(images))
  })

// ── Flags / args ────────────────────────────────────────────────────────

const filesArg = Argument.file("file", { mustExist: true }).pipe(
  Argument.withDescription("Path to an image file (png, jpg, webp, gif, svg, avif, heic, bmp, tiff)"),
  Argument.variadic({ min: 1 }),
)

const titleFlag = Flag.string("title").pipe(
  Flag.withDescription(
    "Image title (single file). If set, your title + --description are stored verbatim and the AI describer does NOT run — anything you leave blank stays blank. Omit --title to let the server auto-generate BOTH title and description from the image.",
  ),
  Flag.optional,
)

const descriptionFlag = Flag.string("description").pipe(
  Flag.withDescription(
    "Image description / alt text (single file; requires --title). Without it and with --title set, the image has no alt text.",
  ),
  Flag.optional,
)

const noOptimizeFlag = Flag.boolean("no-optimize").pipe(
  Flag.withDescription("Upload the original bytes unchanged (skip resize + re-encode)"),
)

const maxEdgeFlag = Flag.integer("max-edge").pipe(
  Flag.withDescription("Longest-edge resize cap in pixels (default 2000)"),
  Flag.optional,
)

// ── Commands ────────────────────────────────────────────────────────────

export const imageUploadCommand = Command.make(
  "upload",
  {
    file: filesArg,
    title: titleFlag,
    description: descriptionFlag,
    noOptimize: noOptimizeFlag,
    maxEdge: maxEdgeFlag,
    json: jsonFlag,
    dir: dirFlag,
    apiUrl: apiUrlFlag,
  },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, () =>
      runUpload({
        files: flags.file,
        title: flags.title,
        description: flags.description,
        optimize: !flags.noOptimize,
        maxEdge: flags.maxEdge,
        json: flags.json,
      }),
    ),
).pipe(
  Command.withDescription(
    "Optimize and upload image(s) to the workspace, printing the public URL to paste into an <img src>",
  ),
)

export const imageListCommand = Command.make(
  "list",
  { json: jsonFlag, dir: dirFlag, apiUrl: apiUrlFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, () =>
      runList({ json: flags.json }),
    ),
).pipe(Command.withDescription("List the workspace's images (title, public URL)"))

export const imagesCommand = Command.make("images").pipe(
  Command.withDescription("Upload and manage workspace images"),
  Command.withSubcommands([imageUploadCommand, imageListCommand]),
)
